import { POSTAPI } from "../config/Api";
import { ApiConstants } from "../constants/ApiConstants";
import { PAGINATION } from "../constants/Constants";

function draftRowHasProgress(res: any): boolean {
  const t = (v: unknown) => String(v ?? "").trim();
  if (t(res?.part_number)) return true;
  if (t(res?.description)) return true;
  if (t(res?.serial_number)) return true;
  if (t(res?.model_number)) return true;
  if (t(res?.sku)) return true;
  const brand = t(res?.brand_name);
  if (brand && brand !== "0") return true;
  if (brand === "0" && t(res?.other_brand_name)) return true;
  const qty = Number(res?.quantity);
  if (Number.isFinite(qty) && qty > 0) return true;
  if (t(res?.file_path) || t(res?.file_path2) || t(res?.video_path)) return true;
  return false;
}

function draftRowQuantity(res: any): number | string {
  const qty = Number(res?.quantity);
  if (Number.isFinite(qty) && qty > 0) return qty;
  return 1;
}

/** Same shape as manual quote `QuotesAdd` / `drafts/save` line items. */
export function formRowsToQuotesCreatePayload(data: { quotes?: any[] } | null | undefined): any[] {
  const quotes: any[] = [];
  data?.quotes?.forEach((res: any) => {
    if (!draftRowHasProgress(res)) return;
    const media_upload_order = Array.isArray(res?.media_upload_order)
      ? res.media_upload_order.filter(
          (k: any) => k === "image1" || k === "image2" || k === "video",
        )
      : [];
    quotes.push({
      brand_id: res?.brand_name,
      quantity: draftRowQuantity(res),
      part_number: res?.part_number,
      sku: res?.sku != null && String(res.sku).trim() !== "" ? String(res.sku).trim() : null,
      description: res?.description,
      serial_number: res?.serial_number,
      model_number: res?.model_number,
      file_path: res?.file_path,
      video_path: res?.video_path,
      file_path2: res?.file_path2 || null,
      thumb_file_path: null,
      other_brand_name: res?.other_brand_name ? res?.other_brand_name : null,
      is_other_brand: res?.brand_name == "0" ? "1" : "0",
      media_upload_order,
    });
  });
  return quotes;
}

function mediaList(q: any): any[] {
  return Array.isArray(q?.media_detail) ? q.media_detail : [];
}

export function sortDraftQuoteRows<T extends { id?: unknown; created_at?: unknown; createdAt?: unknown }>(
  rows: T[],
): T[] {
  return [...rows].sort((a, b) => {
    const ta = new Date(String(a?.created_at ?? a?.createdAt ?? 0)).getTime();
    const tb = new Date(String(b?.created_at ?? b?.createdAt ?? 0)).getTime();
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
  });
}

/** Map stored draft brand fields back to wizard `brand_name` ("" vs "0" for Other Brand). */
export function draftStoredBrandToFormBrandName(plain: {
  brand_id?: unknown;
  is_other_brand?: unknown;
  other_brand_name?: unknown;
}): string {
  const numericBrandId = Number(plain?.brand_id);
  if (Number.isFinite(numericBrandId) && numericBrandId > 0) {
    return String(numericBrandId);
  }
  const isOther = String(plain?.is_other_brand ?? "0") === "1";
  const otherName = String(plain?.other_brand_name ?? "").trim();
  if (isOther || otherName) {
    return "0";
  }
  return "";
}

export function mapQuotesDetailToWizardFormRows(quotesRaw: any[]): any[] {
  return sortDraftQuoteRows(quotesRaw || []).map((q: any) => {
    const plain = q?.toJSON ? q.toJSON() : q;
    const media = mediaList(plain)
      .slice()
      .sort((a: any, b: any) => Number(a?.media_id ?? 0) - Number(b?.media_id ?? 0));
    const media_upload_order: Array<"image1" | "image2" | "video"> = [];
    const imgPaths: string[] = [];
    let vp = "";
    media.forEach((m: any) => {
      const path = String(m?.file_path ?? "").trim();
      if (!path) return;
      if (String(m?.media_type || "").toLowerCase() === "video") {
        if (!vp) {
          vp = path;
          media_upload_order.push("video");
        }
        return;
      }
      if (imgPaths.length === 0) {
        imgPaths.push(path);
        media_upload_order.push("image1");
      } else if (imgPaths.length === 1) {
        imgPaths.push(path);
        media_upload_order.push("image2");
      }
    });
    const fp = imgPaths[0] || String(plain?.file_path ?? "").trim() || "";
    const fp2 = imgPaths[1] || String(plain?.file_path2 ?? "").trim() || "";
    if (!vp) {
      vp = String(plain?.video_path ?? "").trim() || "";
    }
    const brand_name = draftStoredBrandToFormBrandName(plain);
    return {
      media_type: "one",
      sku: plain?.sku ?? "",
      brand_name,
      part_number: plain?.part_number ?? "",
      quantity: plain?.quantity ?? "",
      description: plain?.description ?? "",
      serial_number: plain?.serial_number ?? "",
      model_number: plain?.model_number ?? "",
      other_brand_name: plain?.other_brand_name ?? "",
      file_path: fp,
      file_path2: fp2,
      video_path: vp,
      media_upload_order,
      is_other_brand: brand_name === "0" ? "1" : "0",
    };
  });
}

export function serverDraftRowToQuotesCreatePayload(row: any): any[] {
  const formRows = mapQuotesDetailToWizardFormRows(Array.isArray(row?.quotes_detail) ? row.quotes_detail : []);
  return formRowsToQuotesCreatePayload({ quotes: formRows });
}

export function inferWizardStepFromDraftRow(row: any): 1 | 2 | 3 {
  const aid = Number(row?.address_id ?? 0);
  const sid = Number(row?.shipping_id ?? 0);
  if (aid > 0 && sid > 0) return 2;
  return 1;
}

export function mapServerDraftRowToHydrateParts(row: any) {
  const qlist = Array.isArray(row?.quotes_detail) ? row.quotes_detail : [];
  const aid = row?.address_id != null && Number(row.address_id) > 0 ? String(row.address_id) : "";
  const sid = row?.shipping_id != null && Number(row.shipping_id) > 0 ? String(row.shipping_id) : "";
  return {
    quotes: mapQuotesDetailToWizardFormRows(qlist),
    shippingData: { address_id: aid, shipping_id: sid },
    shippingMeta: {
      residence_type: "" as const,
      billing_same: false,
      delivery_instructions: "",
    },
    step: inferWizardStepFromDraftRow(row),
    quotationId: row?.quotation_id != null ? String(row.quotation_id) : undefined,
    draftInternalId:
      row?.draft_id != null ? String(row.draft_id) : row?.id != null ? String(row.id) : undefined,
  };
}

function draftListRows(res: any): any[] {
  const inner = res?.data;
  if (Array.isArray(inner?.rows)) return inner.rows;
  if (Array.isArray(inner?.data?.rows)) return inner.data.rows;
  return [];
}

export async function fetchServerDraftRowByQuotationSearch(search: string): Promise<any | null> {
  const s = String(search ?? "").trim();
  if (!s) return null;
  const res = await POSTAPI(
    ApiConstants.QuotesDraftList,
    {
      search: s,
      page: PAGINATION.PAGE,
      pageSize: 10,
      orderDirection: "DESC",
    },
    false
  );
  if (!res?.status) return null;
  const rows = draftListRows(res);
  if (!rows.length) return null;
  const exact =
    rows.find((r: any) => String(r?.quotation_id) === s) ||
    rows.find((r: any) => String(r?.quotation_id ?? "").startsWith(s)) ||
    rows[0];
  return exact ?? null;
}

export async function deleteServerDraftByQuotationId(quotationId: string | null | undefined): Promise<boolean> {
  if (!quotationId) return false;
  const res = await POSTAPI(ApiConstants.QuotesDraftDelete, { quotation_id: String(quotationId) }, false);
  return Boolean(res?.status);
}
